import { useCallback, useEffect, useRef, useState } from 'react'
import type { MicDenial } from './mic-help'
import { stopVoice } from './voice-playback'

export type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'paused' | 'preview' | 'denied' | 'unsupported'

export type VoiceRecording = {
  readonly blob: Blob
  readonly url: string
  readonly durationMs: number
  readonly peaks: number[]
}

export type VoiceRecorder = {
  readonly status: RecorderStatus
  readonly denial: MicDenial | null
  readonly elapsedMs: number
  readonly levels: readonly number[]
  readonly recording: VoiceRecording | null
  readonly start: () => Promise<void>
  readonly stop: () => void
  readonly pause: () => void
  readonly resume: () => void
  readonly discard: () => void
}

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus']
const LEVEL_EVERY_MS = 100
const KEEP_LEVELS = 600

export function voiceRecordingSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
}

function denialOf(e: unknown): MicDenial {
  const name = e instanceof DOMException ? e.name : ''
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'no-device'
  if (name === 'NotReadableError' || name === 'AbortError') return 'busy'
  return 'blocked'
}

function pickMime(): string {
  return MIME_CANDIDATES.find((m) => window.MediaRecorder.isTypeSupported(m)) ?? ''
}

function downsample(levels: readonly number[], count: number): number[] {
  if (levels.length === 0) return []
  return Array.from({ length: count }, (_, i) => {
    const from = Math.floor((i / count) * levels.length)
    const to = Math.max(from + 1, Math.floor(((i + 1) / count) * levels.length))
    return Math.max(...levels.slice(from, to))
  })
}

export function useVoiceRecorder(maxMs: number): VoiceRecorder {
  const [status, setStatus] = useState<RecorderStatus>(() => (voiceRecordingSupported() ? 'idle' : 'unsupported'))
  const [denial, setDenial] = useState<MicDenial | null>(null)
  const [elapsedMs, setElapsed] = useState(0)
  const [levels, setLevels] = useState<number[]>([])
  const [recording, setRecording] = useState<VoiceRecording | null>(null)
  const media = useRef<{ recorder: MediaRecorder; stream: MediaStream; ctx: AudioContext; timer: number } | null>(null)
  const chunks = useRef<Blob[]>([])
  const clock = useRef({ startedAt: 0, accumulated: 0 })
  const allLevels = useRef<number[]>([])

  const release = useCallback(() => {
    const m = media.current
    if (!m) return
    window.clearInterval(m.timer)
    m.stream.getTracks().forEach((t) => t.stop())
    void m.ctx.close().catch(() => undefined)
    media.current = null
  }, [])

  const elapsed = () => clock.current.accumulated + (clock.current.startedAt ? performance.now() - clock.current.startedAt : 0)

  const stop = useCallback(() => {
    const m = media.current
    if (!m || m.recorder.state === 'inactive') return
    clock.current = { startedAt: 0, accumulated: elapsed() }
    m.recorder.stop()
  }, [])

  const start = useCallback(async () => {
    if (!voiceRecordingSupported()) {
      setStatus('unsupported')
      return
    }
    stopVoice()
    setDenial(null)
    setStatus('requesting')
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
    } catch (e) {
      setDenial(denialOf(e))
      setStatus('denied')
      return
    }
    const mime = pickMime()
    const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream)
    const ctx = new AudioContext()
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 512
    ctx.createMediaStreamSource(stream).connect(analyser)
    const buf = new Uint8Array(analyser.fftSize)
    chunks.current = []
    allLevels.current = []
    clock.current = { startedAt: performance.now(), accumulated: 0 }
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.current.push(e.data)
    }
    recorder.onstop = () => {
      const durationMs = clock.current.accumulated
      const blob = new Blob(chunks.current, { type: recorder.mimeType || mime || 'audio/webm' })
      release()
      setRecording({ blob, url: URL.createObjectURL(blob), durationMs, peaks: downsample(allLevels.current, 64) })
      setStatus('preview')
    }
    const timer = window.setInterval(() => {
      if (recorder.state !== 'recording') return
      analyser.getByteTimeDomainData(buf)
      let peak = 0
      for (const v of buf) peak = Math.max(peak, Math.abs(v - 128))
      const level = Math.min(100, Math.round((peak / 128) * 160) + 6)
      allLevels.current.push(level)
      setLevels(allLevels.current.slice(-KEEP_LEVELS))
      const ms = elapsed()
      setElapsed(ms)
      if (ms >= maxMs) {
        clock.current = { startedAt: 0, accumulated: maxMs }
        recorder.stop()
      }
    }, LEVEL_EVERY_MS)
    media.current = { recorder, stream, ctx, timer }
    recorder.start(1000)
    setElapsed(0)
    setLevels([])
    setStatus('recording')
  }, [maxMs, release])

  const pause = useCallback(() => {
    const m = media.current
    if (m?.recorder.state !== 'recording') return
    m.recorder.pause()
    clock.current = { startedAt: 0, accumulated: elapsed() }
    setStatus('paused')
  }, [])

  const resume = useCallback(() => {
    const m = media.current
    if (m?.recorder.state !== 'paused') return
    m.recorder.resume()
    clock.current = { startedAt: performance.now(), accumulated: clock.current.accumulated }
    setStatus('recording')
  }, [])

  const discard = useCallback(() => {
    const m = media.current
    if (m && m.recorder.state !== 'inactive') {
      m.recorder.onstop = null
      m.recorder.stop()
    }
    release()
    stopVoice()
    setRecording((r) => {
      if (r) URL.revokeObjectURL(r.url)
      return null
    })
    setElapsed(0)
    setLevels([])
    setStatus(voiceRecordingSupported() ? 'idle' : 'unsupported')
  }, [release])

  useEffect(() => () => release(), [release])

  return { status, denial, elapsedMs, levels, recording, start, stop, pause, resume, discard }
}
